'use client';

import React, {createContext, useContext, useEffect} from 'react';
import {useCompletedWalkthroughStepKeys} from '../hooks/queries';
import {LOCAL_STORAGE_VALUES, WALKTHROUGH_STEPKEYS} from '../utils/constants';
import {
  BENEFIT_AVAILABILITY_TYPES,
  BENEFIT_REVENUE_TYPES,
  COMMISSION_TYPES,
  DISCOUNT_CODE_TYPES
} from '@/app/[locale]/dashboard/@merchant/benefits/create/_components/_types';
import {useUser} from './UserContext';
import {useLocalStorage} from '@reactuses/core';
import {usePathname} from '../i18n/navigation';
import {ROUTES} from '../utils/routes';

interface BenefitInfo {
  [key: string]: any;
}

interface NewBenefitContextType {
  benefit: BenefitInfo | null;
  setBenefit: (value: BenefitInfo) => void;
  currentStep: number | null;
  setCurrentStep: (value: number) => void;
  resetContext: () => void;
}

const NewBenefitContext = createContext<NewBenefitContextType | undefined>(
  undefined
);

export function NewBenefitProvider({children}: {children: React.ReactNode}) {
  const pathname = usePathname();

  const {userRole} = useUser();
  const {data: completedWalkthroughStepKeys} = useCompletedWalkthroughStepKeys({
    enabled: !!userRole
  });

  const initialState = {
    benefitType: BENEFIT_REVENUE_TYPES.cashback,
    codeType: DISCOUNT_CODE_TYPES.shared,
    isPublic:
      !completedWalkthroughStepKeys?.includes(
        WALKTHROUGH_STEPKEYS.merchant.createBenefit
      ) || false,
    discount: {
      availabilityType: BENEFIT_AVAILABILITY_TYPES.time
    },
    cap: {
      type: COMMISSION_TYPES.percentage
    }
  };

  const [currentStep, setCurrentStep] = useLocalStorage<number>(
    LOCAL_STORAGE_VALUES.benefit.creationStep,
    0
  );
  const [benefit, setBenefit] = useLocalStorage<BenefitInfo>(
    LOCAL_STORAGE_VALUES.benefit.newState,
    initialState
  );

  const resetContext = () => {
    setCurrentStep(0);
    setBenefit(initialState);
  };

  useEffect(() => {
    if (!pathname.includes(ROUTES.createBenefits)) {
      resetContext();
    }
  }, [pathname]);

  return (
    <NewBenefitContext.Provider
      value={{
        benefit,
        setBenefit,
        currentStep,
        setCurrentStep,
        resetContext
      }}
    >
      {children}
    </NewBenefitContext.Provider>
  );
}

export function useNewBenefit() {
  const context = useContext(NewBenefitContext);
  if (context === undefined) {
    throw new Error('useNewBenefit must be used within a NewBenefitProvider');
  }
  return context;
}
